import React, { FC, useEffect, useState } from 'react';
import { Switch, View } from 'react-native';

import { downloadReminderCalendar, openGoogleCalendarReminder } from '../../utils/challenges/reminderCalendar';
import { isWeb } from '../../config/designSystem/Layout';

import DesignSystemButton from '../Button/DesignSystemButton';
import { COLORS } from '../../config/designSystem/Colors';
import { CongratsBadge } from './CongratsBadge';
import { H4, ParagraphMBold, ParagraphMRegular } from '../../config/designSystem/typography';
import { SPACING } from '../../config/designSystem/Spacing';
import { SelectField } from '../Diary/ui/SelectField';
import { TouchableText } from '../TouchableText/TouchableText';

/**
 * figma: ❖ Reminders screen (Activities & Challenges pt1, node 9543-48417):
 * "Let's turn your intention into a habit!" — bell hexagon, reminders toggle, schedule
 * time and a single "I am all set up!" CTA. Shown inside the session right
 * after a challenge is accepted.
 */
export interface ReminderSettings {
  enabled: boolean;
  time: string;
}

interface ReminderFrameProps {
  designSystemColor?: string | null;
  defaultTime?: string;
  defaultEnabled?: boolean;
  /** the settings as they are edited: the session's Continue saves them */
  onChange: (settings: ReminderSettings) => void;
  /** web: what the calendar file describes (no local notifications there) */
  calendar?: { title: string; days: number };
  /** shown as a link: leave the challenge */
  onRemove?: () => void;
  removeLabel?: string;
  labels?: {
    MAKE_IT_REAL?: string;
    REMINDER_HELPS?: string;
    ACTIVATE_REMINDERS?: string;
    SCHEDULE?: string;
    CALENDAR_HINT?: string;
    ADD_TO_GOOGLE_CALENDAR?: string;
    ADD_TO_CALENDAR?: string;
    CALENDAR_ADDED?: string;
  };
}

const TIME_OPTIONS = [
  '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM',
  '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM',
];

export const ReminderFrame: FC<ReminderFrameProps> = ({
  designSystemColor,
  defaultTime = '6:00 PM',
  defaultEnabled = true,
  onChange,
  calendar,
  onRemove,
  removeLabel,
  labels,
}) => {
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [time, setTime] = useState(defaultTime);
  // web: which calendar action was taken, to confirm it on screen
  const [calendarAdded, setCalendarAdded] = useState(false);
  useEffect(() => {
    onChange({ enabled, time });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, time]);

  return (
    <View
      style={{
        flex: 1,
        paddingHorizontal: SPACING['sp-16'],
        rowGap: SPACING['sp-24'],
      }}
    >
      <View style={{ flex: 1, justifyContent: 'center', rowGap: SPACING['sp-24'] }}>
        <View style={{ alignItems: 'center' }}>
          <CongratsBadge kind="bell" designSystemColor={designSystemColor} size={150} />
        </View>
        <H4 align={true}>{labels?.MAKE_IT_REAL || 'Let\'s turn your intention into a habit!'}</H4>
        <ParagraphMRegular align={true} color={COLORS.content.secondary}>
          {labels?.REMINDER_HELPS || 'Adding a reminder helps you achieve your goals.'}
        </ParagraphMRegular>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <ParagraphMBold>{labels?.ACTIVATE_REMINDERS || 'Activate reminders'}</ParagraphMBold>
          <Switch
            value={enabled}
            onValueChange={setEnabled}
            trackColor={{ true: COLORS.content.accent, false: COLORS.border.secondary }}
            thumbColor={COLORS.surface.contrast}
          />
        </View>
        {enabled && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              columnGap: SPACING['sp-16'],
            }}
          >
            <ParagraphMBold>{labels?.SCHEDULE || 'Schedule'}</ParagraphMBold>
            {/* SelectField's flex:1 root expects a ROW parent — in a column
                parent it collapses and renders an empty field */}
            <View style={{ width: 160, flexDirection: 'row' }}>
              <SelectField
                label=""
                value={time}
                options={TIME_OPTIONS}
                onChange={setTime}
              />
            </View>
          </View>
        )}
      </View>
      {isWeb && enabled && !!calendar && (
        // the web cannot notify: the reminder goes to the user's calendar —
        // a Google Calendar link (prefilled event, nothing to import) or an
        // .ics file for Apple Calendar / Outlook, both confirmed on screen
        <View style={{ rowGap: SPACING['sp-16'] }}>
          <ParagraphMRegular align={true} color={COLORS.content.secondary}>
            {labels?.CALENDAR_HINT
              || 'On the web, Evoluno cannot send notifications: add the daily reminder to your calendar instead.'}
          </ParagraphMRegular>
          <DesignSystemButton
            label={labels?.ADD_TO_GOOGLE_CALENDAR || 'Add to Google Calendar'}
            onPress={() => {
              openGoogleCalendarReminder(calendar.title, time, calendar.days);
              setCalendarAdded(true);
            }}
          />
          <View style={{ alignItems: 'center' }}>
            <TouchableText
              onPress={() => {
                downloadReminderCalendar(calendar.title, time, calendar.days);
                setCalendarAdded(true);
              }}
              label={labels?.ADD_TO_CALENDAR || 'Download for Apple Calendar or Outlook (.ics)'}
            />
          </View>
          {calendarAdded && (
            <ParagraphMBold align={true} color={COLORS.content.accent}>
              {labels?.CALENDAR_ADDED
                || 'Reminder added to your calendar. If a file was downloaded, open it to confirm.'}
            </ParagraphMBold>
          )}
        </View>
      )}
      {!!onRemove && (
        <View style={{ alignItems: 'center' }}>
          <TouchableText onPress={onRemove} label={removeLabel || 'Remove from my daily challenges'} />
        </View>
      )}
    </View>
  );
};
